import React, { useMemo, useState } from 'react';
import { CampaignItem, CampaignItemPipelineStage } from '../../types';
import '../CampaignItemsListModal/CampaignItemsListModal.css';
import './CampaignItemsListPanel.css';

type StageFilter = 'all' | CampaignItemPipelineStage;

const STAGE_OPTIONS: { value: StageFilter; label: string }[] = [
  { value: 'all', label: 'Todos os estágios' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'disparado', label: 'Disparado' },
  { value: 'sucesso', label: 'Sucesso' },
  { value: 'perdido', label: 'Perdido' },
  { value: 'encerrado', label: 'Encerrado' },
];

const REASON_LABEL: Record<string, string> = {
  optout: 'Opt-out',
  posvendas: 'Pós-vendas',
  perdido: 'Perdido',
  duplicado_mesma_campanha: 'Duplicado',
  invalido: 'Inválido',
};

function resolveStatus(item: CampaignItem): 'pendente' | 'disparado' | 'nao_disparado' {
  // "Perdido" significa que já houve disparo, mas sem retorno.
  // Mantemos o status primário como disparado para refletir envio efetivo.
  if (item.pipelineStage === 'perdido' || item.reason === 'perdido') return 'disparado';
  if (item.skipReason === 'unsubscribed' || item.skipReason === 'pos_vendas_active') return 'nao_disparado';
  if (item.status) return item.status;
  return item.pipelineStage === 'pendente' ? 'pendente' : 'disparado';
}

function resolveReason(item: CampaignItem): string | undefined {
  if (item.reason) return item.reason;
  if (item.skipReason === 'unsubscribed') return 'optout';
  if (item.skipReason === 'pos_vendas_active') return 'posvendas';
  if (item.pipelineStage === 'perdido') return 'perdido';
  return undefined;
}

export interface CampaignItemsListPanelProps {
  items: CampaignItem[];
  /** Classe extra no wrapper da toolbar + tabela (ex.: painel embebido no drawer). */
  className?: string;
  /** Texto de ajuda sobre remarketing / transição automática. */
  showRemarketingHint?: boolean;
}

export const CampaignItemsListPanel: React.FC<CampaignItemsListPanelProps> = ({
  items,
  className = '',
  showRemarketingHint = true,
}) => {
  const [q, setQ] = useState('');
  const [stage, setStage] = useState<StageFilter>('all');

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    const digits = q.replace(/\D/g, '');
    return items.filter((it) => {
      if (stage !== 'all' && it.pipelineStage !== stage) return false;
      if (n) {
        const name = (it.nome || '').toLowerCase();
        const phone = (it.telefone || '').replace(/\D/g, '');
        if (!name.includes(n) && !(digits && phone.includes(digits))) return false;
      }
      return true;
    });
  }, [items, q, stage]);

  return (
    <div className={`campaign-items-list-panel-root ${className}`.trim()}>
      {showRemarketingHint && (
        <p className="campaign-items-panel-hint">
          Quando o último contacto da campanha é disparado, os itens passam automaticamente a «perdido» com o prazo de
          remarketing definido nas Configurações da organização (ou 12 meses por omissão).
        </p>
      )}
      <div className="campaign-items-modal-toolbar">
        <label className="campaign-items-modal-field">
          <span>Filtrar por nome ou telefone</span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nome ou telefone…"
          />
        </label>
        <label className="campaign-items-modal-field">
          <span>Estágio</span>
          <select value={stage} onChange={(e) => setStage(e.target.value as StageFilter)}>
            {STAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="campaign-items-modal-table-wrap">
        {filtered.length === 0 ? (
          <p className="campaign-items-modal-empty">Nenhum item com estes filtros.</p>
        ) : (
          <table className="campaign-items-modal-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Telefone</th>
                <th>Estágio</th>
                <th>Disparado</th>
                <th>Origem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.campaignItemId}>
                  <td>{it.nome}</td>
                  <td>{it.telefone || '—'}</td>
                  <td>
                    <span className={`campaign-items-stage status-${it.pipelineStage}`}>{resolveStatus(it)}</span>
                    {resolveReason(it) ? (
                      <span className="campaign-items-stage" style={{ marginLeft: 6 }}>
                        {REASON_LABEL[resolveReason(it) as string] || resolveReason(it)}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {it.disparadoAt ? new Date(it.disparadoAt).toLocaleString('pt-BR') : '—'}
                  </td>
                  <td>
                    {it.sourceType === 'lost_card' ? `Lost ${it.sourceId || ''}` : it.sourceType}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
